import {Button, ButtonSpinner, Dropdown, Checkbox, Form, Input, Locale, SelectOptionType, Slideout, TextArea} from '@local/ui/components'
import {cx} from '@local/utils/cx'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import {ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import {EMAIL_PATTERN, emailIsValid, slugify} from '@local/utils/string'
import {useTranslate} from '@local/hooks'
import PhoneNumberInput from '@local/ui/components/phoneNumberInput/PhoneNumberInput'

interface PrivateRetreatSlideoutProps {
	listID: string
	successText: string
	successTitle: string
	tripLengths: Array<string>
	guestCounts: Array<string>
	priceRanges: Array<string>
	tripCategories: Array<string>
	howDidYouHearQuestions: Array<string>
	isOpen: boolean
	onClickClose: () => void
}

const generateMonthOptions = ():Array<SelectOptionType> => {
	const today = new Date()
	const months: Array<SelectOptionType> = []

	for (let i = 1; i <= 12; i++) {
		let year = today.getFullYear()
		let month = today.getMonth() + i
		if (month > 11) {
			year++
			month -= 12
		}
		const date = new Date(year, month, 1)
		const monthLabel = date.toLocaleString('default', {month: 'long'})
		months.push({label: `${monthLabel} ${year}`, value: `${year}-${(month + 1).toString().padStart(2, '0')}`})
	}

	return months
}

export const PrivateRetreatSlideout = forwardRef(
	(
		{
			isOpen = false,
			onClickClose,
			listID,
			successText,
			successTitle,
			guestCounts,
			priceRanges,
			tripCategories,
			tripLengths,
			howDidYouHearQuestions,
		}:PrivateRetreatSlideoutProps,
		ref:ForwardedRef<HTMLDivElement>,
	) => {
		const locale = useTranslate()

		const monthOptions = useMemo(() => generateMonthOptions(), [])
		const tripLengthOptions = useMemo(() => tripLengths?.map((label) => ({label, value: slugify(label)})), [tripLengths])
		const guestCountsOptions = useMemo(() => guestCounts?.map((label) => ({label, value: slugify(label)})), [guestCounts])
		const priceRangesOptions = useMemo(() => priceRanges?.map((label) => ({label, value: slugify(label)})), [priceRanges])
		const tripCategoriesOptions = useMemo(() => tripCategories?.map((label) => ({label, value: slugify(label)})), [tripCategories])

		const [inputs, setInputs] = useState({
			location: '',
			firstname: '',
			lastname: '',
			email: '',
			phoneNumber: '',
			countryCode: '',
			howDidYouHearAboutUs: '',
			whatAreYouInterested: '',
			subscribeMail: false,

		})
		const [isLoading, setIsLoading] = useState<boolean>(false)
		const [isSuccess, setIsSuccess] = useState<boolean>(false)
		const [hasError, setHasError] = useState<boolean>(false)

		const formRef = useRef<HTMLFormElement>(null)
		const [checkboxInvalid, setCheckboxInvalid] = useState({
			month: false,
			tripLength: false,
			guestCount: false,
			priceRange: false,
			tripCategory: false,
		})
		const handleInput = (value:string, name:string) => {
			setInputs((prev) => ({...prev, [name]: value}))
		}

		const handleReset = () => {
			setInputs({
				location: '',
				firstname: '',
				lastname: '',
				email: '',
				phoneNumber: '',
				countryCode: '',
				howDidYouHearAboutUs: '',
				whatAreYouInterested: '',
				subscribeMail: false,
			})
			setCheckboxInvalid({
				month: false,
				tripLength: false,
				guestCount: false,
				priceRange: false,
				tripCategory: false,
			})
		}

		const handleSubmit = async ({body}) => {
			setIsLoading(true)
			try {
				setIsSuccess(false)
				setHasError(false)

				Object.keys(checkboxInvalid).forEach((key) => {
					const isInvalid = Boolean(document.querySelector(`input[name="${key}"]:checked`))
					setCheckboxInvalid((prev) => ({...prev, [key]: !isInvalid}))
					if (!isInvalid) {
						throw new Error('InvalidCheckbox')
					}
				})
				if (!emailIsValid(body.email)) {
					throw new Error('InvalidEmail')
				}

				if (body.subscribeMail) {
					await fetch('/api/newsletter/subscribe/', {
						method: 'POST',
						body: JSON.stringify({email_address: body.email, listID}),
					})
				}

				const res = await fetch('/api/contact/', {
					method: 'POST',
					body: JSON.stringify({
						listID,
						emailAddress: body.email,
						phone: body.countryCode + body.phoneNumber,
						firstName: body.firstname,
						lastName: body.lastname,
						guestCount: body.guestCount,
						month: body.month,
						location: body.location,
						priceRange: body.priceRange,
						tripCategory: body.tripCategory,
						tripLength: body.tripLength,
						howDidYouHearAboutUs: body.howDidYouHearAboutUs,
						whatAreYouInterested: body.whatAreYouInterested,
					}),
				})
				const result = await res.json()

				if (res.status === 200) {
					setIsSuccess(true)
					setIsLoading(false)
					return {status: 200}
				} else {
					setIsLoading(false)
					setHasError(true)
					return {errors: [result]}
				}
			} catch (error) {
				setIsLoading(false)
				setHasError(true)

				if (error.message === 'InvalidEmail') {
					return {errors: [{code: 400, message: 'Invalid email'}]}
				}
				if (error.message === 'InvalidCheckbox') {
					return {errors: [{code: 400, message: 'Invalid input'}]}
				}
				if (error.message === 'RequestFailed') {
					return {errors: [{code: 500, message: 'Request failed'}]}
				}
			}
		}

		useEffect(() => {
			if (isSuccess) {
				setTimeout(() => {
					onClickClose()
					setIsSuccess(false)
				}, 2000)
			}
		}, [isSuccess])

		return (
			<Slideout
				ref={ref}
				variant='right'
				isOpen={isOpen}
				onClickClose={onClickClose}
				className='bg-grey-4 text-white relative !px-0'>
				<div data-lenis-prevent className='overflow-y-scroll min-h-screen h-fit margin-px-1 flex flex-col scrollbar-hide'>
					<div className='sticky top-0 z-6 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
						<h2 className='w-fit uppercase'>{locale('Have a question?')}</h2>
						<Button
							small
							variant='icon'
							onClick={onClickClose}
							className='px-16 aspect-square'>
							<CloseSVG />
						</Button>
					</div>
					<Form
						ref={formRef}
						autoReset
						additionalReset={handleReset}
						onSubmit={handleSubmit}
						successMessage={<SuccessPanel title={successTitle} text={successText} />}
						errorClassName='bottom-8 text-center'
						successClassName='z-5 mt-0 -inset-1 bg-grey-4 w-[calc(100%+2px)]'
						className={cx(
							'flex flex-col h-fit w-full pt-40 gap-50',
							'[&_h3]:font-normal [&_h3]:text-18',
						)}>
						<div className='relative flex flex-col gap-20'>
							<h3>
								<Locale>When would you like to go?</Locale>
							</h3>
							<span className={cx(
								'text-12 text-red absolute top-28',
								'opacity-0 transition-opacity duration-150',
								checkboxInvalid.month && 'opacity-100',
							)}>
								<Locale>Select at least one option</Locale>
							</span>
							<div className='grid grid-cols-3 gap-10'>
								{monthOptions.map((option) => (
									<label
										key={option.value}
										htmlFor={option.value}>
										<input
											type='checkbox'
											name='month'
											value={option.value}
											id={option.value}
											className='sr-only peer' />
										<span
											className={cx(
												'bg-transparent flex items-center justify-center h-48 text-12',
												'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
												'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
												'peer-invalid:border-red',
											)}>
											{option.label}
										</span>
									</label>
								))}
							</div>
						</div>
						<div className='relative flex flex-col gap-20'>
							<h3>
								<Locale>What's the planned duration of your trip?</Locale>
							</h3>
							<span className={cx(
								'text-12 text-red absolute top-28',
								'opacity-0 transition-opacity duration-150',
								checkboxInvalid.tripLength && 'opacity-100',
							)}>
								<Locale>Select one option</Locale>
							</span>
							<div className='grid grid-cols-2 gap-10'>
								{tripLengthOptions.map((option) => (
									<label
										key={option.value}
										htmlFor={option.value}>
										<input
											type='radio'
											name='tripLength'
											value={option.value}
											id={option.value}
											className='sr-only peer' />
										<span
											className={cx(
												'bg-transparent flex items-center justify-center h-48 text-12',
												'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
												'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
											)}>
											{option.label}
										</span>
									</label>
								))}
							</div>
						</div>
						<div className='relative flex flex-col gap-20'>
							<h3>
								<Locale>How many guests do you anticipate will come?</Locale>
							</h3>
							<span className={cx(
								'text-12 text-red absolute top-28',
								'opacity-0 transition-opacity duration-150',
								checkboxInvalid.guestCount && 'opacity-100',
							)}>
								<Locale>Select one option</Locale>
							</span>
							<div className='grid grid-cols-2 gap-10'>
								{guestCountsOptions.map((option) => (
									<label
										key={option.value}
										htmlFor={option.value}>
										<input
											type='radio'
											name='guestCount'
											value={option.value}
											id={option.value}
											className='sr-only peer' />
										<span
											className={cx(
												'bg-transparent flex items-center justify-center h-48 text-12',
												'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
												'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
											)}>
											{option.label}
										</span>
									</label>
								))}
							</div>
						</div>
						<div className='relative flex flex-col gap-20'>
							<h3>
								<Locale>What per person pricing are you targeting?</Locale>
							</h3>
							<span className={cx(
								'text-12 text-red absolute top-28',
								'opacity-0 transition-opacity duration-150',
								checkboxInvalid.priceRange && 'opacity-100',
							)}>
								<Locale>Select one option</Locale>
							</span>
							<div className='grid grid-cols-2 gap-10'>
								{priceRangesOptions?.map((option) => (
									<label
										key={option.value}
										htmlFor={option.value}>
										<input
											type='radio'
											name='priceRange'
											value={option.value}
											id={option.value}
											className='sr-only peer' />
										<span
											className={cx(
												'bg-transparent flex items-center justify-center h-48 text-12',
												'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
												'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
											)}>
											{option.label}
										</span>
									</label>
								))}
							</div>
						</div>
						<div className='grid grid-cols-1 gap-10'>
							<h3 className='mb-10'>
								<Locale>Where will the guests be starting their journeys?</Locale>
							</h3>
							<Input
								required
								variant='dark'
								type='text'
								name='location'
								label={locale('Location')}
								value={inputs.location}
								onChange={e => handleInput(e.target.value, 'location')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
						</div>
						<div className='relative flex flex-col gap-20'>
							<h3>
								<Locale>Pick the categories that reflect your trip vision.</Locale>
							</h3>
							<span className={cx(
								'text-12 text-red absolute top-28',
								'opacity-0 transition-opacity duration-150',
								checkboxInvalid.tripCategory && 'opacity-100',
							)}>
								<Locale>Select at least one option</Locale>
							</span>
							<div className='grid grid-cols-3 gap-10'>
								{tripCategoriesOptions.map((option) => (
									<label
										key={option.value}
										htmlFor={option.value}>
										<input
											type='checkbox'
											name='tripCategory'
											value={option.value}
											id={option.value}
											className='sr-only peer' />
										<span
											className={cx(
												'bg-transparent flex items-center justify-center min-h-[3rem] h-full text-12',
												'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
												'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
												'text-center py-10 px-5',
											)}>
											{option.label}
										</span>
									</label>
								))}
							</div>
						</div>

						<div className='grid grid-cols-2 gap-10'>
							<h3 className='col-span-2 mb-10'>
								<Locale>Your details so we can get in touch:</Locale>
							</h3>
							<Input
								required
								variant='dark'
								type='text'
								name='firstname'
								label={locale('Firstname')}
								value={inputs.firstname}
								onChange={e => handleInput(e.target.value, 'firstname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='text'
								name='lastname'
								label={locale('Lastname')}
								value={inputs.lastname}
								onChange={e => handleInput(e.target.value, 'lastname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='email'
								name='email'
								label='Email'
								value={inputs.email}
								onChange={e => handleInput(e.target.value, 'email')}
								onInvalid={() => setHasError(true)}
								pattern={EMAIL_PATTERN}
								hasError={hasError}
								className='h-48 col-span-2'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<PhoneNumberInput
								onChange={(value:string) => handleInput(value, 'phoneNumber')}
								onInvalid={() => setHasError(true)}
								hasError={hasError} />
							<Dropdown
								id='howDidYouHearAboutUs' name='howDidYouHearAboutUs' placeholder='How did you hear about us?'
								options={howDidYouHearQuestions.map((item:string) => {
									return {label: item, value: item.toLowerCase()}
								})} />

							<TextArea
								required
								variant='dark'
								name='whatAreYouInterested'
								label={locale('What are you interested in?')}
								inputClassName='h-full'
								className='h-72 col-span-2'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Checkbox name='subscribeMail' theme='dark' className='col-span-2'>
								<span className='text-12 text-white'>Receive inspiration in your inbox</span>
							</Checkbox>

						</div>

						<div className='pb-25 w-full'>
							<ButtonSpinner
								type='submit'
								small
								loading={isLoading}
								variant='primary-transparent'
								className='mt-auto w-full'>
								<Locale>Send</Locale>
							</ButtonSpinner>
						</div>
					</Form>
				</div>
			</Slideout>
		)
	},
)

const SuccessPanel = ({title, text}) => (
	<div className='flex flex-col items-start pt-40 w-full h-fit gap-14 bg-grey-4 normal-case text-left sticky top-86'>
		<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
		<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{text}</p>
	</div>
)
